// Utility functions for calculating technician capacity
import type {
  SettingsLocationBusinessHours,
  SettingsLocationHoliday,
  EmployeeSchedule,
  EmployeeScheduleException,
} from "../models";
import { isSameLocalCalendarDay } from "./schedular-utils";

/**
 * Calculate hours between two times
 * Handles both Date objects and date strings from API
 */
function calculateHoursBetween(
  beginTime: Date | string | null,
  endTime: Date | string | null,
): number {
  if (!beginTime || !endTime) return 0;

  // Convert to Date objects if they're strings
  const begin = beginTime instanceof Date ? beginTime : new Date(beginTime);
  const end = endTime instanceof Date ? endTime : new Date(endTime);

  // Check if dates are valid
  if (isNaN(begin.getTime()) || isNaN(end.getTime())) {
    return 0;
  }

  const diffMs = end.getTime() - begin.getTime();
  return diffMs / (1000 * 60 * 60); // Convert to hours
}

/**
 * Check if a date is a holiday for any of the selected locations
 */
export function isHoliday(
  date: Date,
  locationIds: number[],
  holidays: SettingsLocationHoliday[],
): boolean {
  return holidays.some((holiday) => {
    if (!locationIds.includes(holiday.locationId)) return false;
    return isSameLocalCalendarDay(holiday.holidayDate, date);
  });
}

/**
 * Check if a location is open on a given day
 */
export function isLocationOpen(
  dayOfWeek: number,
  locationIds: number[],
  businessHours: SettingsLocationBusinessHours[],
): boolean {
  return businessHours.some(
    (bh) =>
      locationIds.includes(bh.locationId) &&
      bh.dayOfWeek === dayOfWeek &&
      bh.beginTime !== null &&
      bh.endTime !== null,
  );
}

/**
 * Get technician's scheduled hours for a specific day
 */
export function getTechnicianScheduledHours(
  appUserId: number,
  dayOfWeek: number,
  employeeSchedules: EmployeeSchedule[],
): number {
  const schedule = employeeSchedules.find(
    (es) => es.appUserId === appUserId && es.dayOfWeek === dayOfWeek,
  );
  if (!schedule || !schedule.beginTime || !schedule.endTime) {
    return 0;
  }
  return calculateHoursBetween(schedule.beginTime, schedule.endTime);
}

/**
 * Get technician's available hours considering exceptions for a specific date
 */
export function getTechnicianAvailableHoursWithException(
  appUserId: number,
  date: Date,
  locationId: number,
  baseCapacity: number,
  employeeScheduleExceptions: EmployeeScheduleException[],
): number {
  const exception = employeeScheduleExceptions.find(
    (exc) =>
      exc.appUserId === appUserId &&
      exc.locationId === locationId &&
      isSameLocalCalendarDay(exc.exceptionDate, date),
  );

  if (!exception) {
    return baseCapacity;
  }

  // If both beginTime and endTime are null, it's a full day exception (0 hours)
  if (!exception.beginTime || !exception.endTime) {
    return 0;
  }

  // If exception has times, calculate available hours
  // This means the technician is available only during the exception time window
  return calculateHoursBetween(exception.beginTime, exception.endTime);
}

/**
 * Get technician's total capacity for a day considering schedule and exceptions
 */
export function getTechnicianDayCapacity(
  appUserId: number,
  dayOfWeek: number,
  date: Date,
  locationId: number,
  employeeSchedules: EmployeeSchedule[],
  employeeScheduleExceptions: EmployeeScheduleException[],
): number {
  // Get base capacity from schedule
  const baseCapacity = getTechnicianScheduledHours(
    appUserId,
    dayOfWeek,
    employeeSchedules,
  );

  // Apply exception if any
  return getTechnicianAvailableHoursWithException(
    appUserId,
    date,
    locationId,
    baseCapacity,
    employeeScheduleExceptions,
  );
}
